import { matchRoleKey } from '@protoss/shared';
import { NumberInput, Select, SelectItem, TextArea, Tooltip } from '@protoss/ui';
import React, { useState } from 'react';

const LEVEL_OPTIONS = ['senior', 'mid', 'junior'];
const CATEGORY_OPTIONS = ['delivery', 'architecture', 'functional', 'technical', 'data', 'quality'];

function toSnakeCase(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

function RoleRateReadRow({
  roleId,
  rates,
}: {
  roleId: string;
  rates: {
    role_name?: string;
    level?: string;
    category?: string;
    onshore: number;
    offshore: number;
  };
}) {
  const displayLabel = rates.role_name || roleId.toUpperCase();
  return (
    <div className="da-option-row">
      <div className="da-option-row__label-group">
        <span className="da-option-row__label-text">{displayLabel}</span>
        {(rates.level || rates.category) && (
          <div className="da-option-row__rate-pill-group">
            {rates.level && (
              <span className="da-option-row__rate-pill capitalize">{rates.level}</span>
            )}
            {rates.category && (
              <span className="da-option-row__rate-pill capitalize">{rates.category}</span>
            )}
          </div>
        )}
      </div>
      <div className="da-option-row__rates">
        <span className="da-option-row__rate-item">
          Onshore:<span className="da-option-row__rate-value">${rates.onshore}</span>
        </span>
        <span className="da-option-row__rate-item">
          Offshore:<span className="da-option-row__rate-value">${rates.offshore}</span>
        </span>
      </div>
    </div>
  );
}

function RoleRateEditRow({
  roleId,
  rates,
  index,
  isDragOver,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  handleRateChange,
  handleUpdateRoleMeta,
  handleDeleteRole,
}: {
  roleId: string;
  rates: {
    role_name?: string;
    level?: string;
    category?: string;
    onshore: number;
    offshore: number;
  };
  index: number;
  isDragOver: boolean;
  onDragStart: (idx: number) => void;
  onDragOver: (idx: number) => void;
  onDragLeave: () => void;
  onDrop: (targetIdx: number) => void;
  handleRateChange: (roleId: string, type: 'onshore' | 'offshore', value: number) => void;
  handleUpdateRoleMeta: (
    roleId: string,
    newRoleName: string,
    level: string,
    category: string
  ) => void;
  handleDeleteRole: (roleId: string) => void;
}) {
  const displayLabel = rates.role_name || roleId.toUpperCase();
  const [draftName, setDraftName] = useState(displayLabel);
  const currentLevel = rates.level || 'mid';
  const currentCategory = rates.category || 'functional';

  React.useEffect(() => {
    setDraftName(rates.role_name || roleId.toUpperCase());
  }, [roleId, rates.role_name]);

  const handleBlur = () => {
    const trimmed = draftName.trim();
    if (trimmed && trimmed !== displayLabel) {
      handleUpdateRoleMeta(roleId, trimmed, currentLevel, currentCategory);
    } else {
      setDraftName(displayLabel);
    }
  };

  return (
    <div
      className={[
        'da-option-row',
        'da-option-row--edit',
        isDragOver ? 'da-option-row--drag-over' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        onDragStart(index);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        onDragOver(index);
      }}
      onDragLeave={onDragLeave}
      onDrop={(e) => {
        e.preventDefault();
        onDrop(index);
      }}
    >
      <span className="da-option-row__drag-handle" aria-hidden="true">
        ⠿
      </span>
      <input
        className="da-option-row__edit-input da-option-row__role-input"
        value={draftName}
        onChange={(e) => setDraftName(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') {
            setDraftName(displayLabel);
            e.currentTarget.blur();
          }
        }}
        aria-label={`Edit role name`}
      />

      <div className="da-option-row__role-meta-select">
        <Select
          value={currentLevel}
          onValueChange={(val) => handleUpdateRoleMeta(roleId, draftName, val, currentCategory)}
          size="sm"
        >
          {LEVEL_OPTIONS.map((opt) => (
            <SelectItem key={opt} value={opt}>
              {opt.charAt(0).toUpperCase() + opt.slice(1)}
            </SelectItem>
          ))}
        </Select>
      </div>

      <div className="da-option-row__role-meta-select da-option-row__role-meta-select--wide">
        <Select
          value={currentCategory}
          onValueChange={(val) => handleUpdateRoleMeta(roleId, draftName, currentLevel, val)}
          size="sm"
        >
          {CATEGORY_OPTIONS.map((opt) => (
            <SelectItem key={opt} value={opt}>
              {opt.charAt(0).toUpperCase() + opt.slice(1)}
            </SelectItem>
          ))}
        </Select>
      </div>

      <div className="da-option-row__rate-edit-group">
        <div className="da-option-row__rate-edit-item">
          <label className="da-option-row__rate-edit-label">Onshore</label>
          <NumberInput
            min={0}
            className="da-option-row__rate-input"
            value={rates.onshore}
            onChange={(e) => handleRateChange(roleId, 'onshore', parseInt(e.target.value, 10))}
          />
        </div>

        <div className="da-option-row__rate-edit-item">
          <label className="da-option-row__rate-edit-label">Offshore</label>
          <NumberInput
            min={0}
            className="da-option-row__rate-input"
            value={rates.offshore}
            onChange={(e) => handleRateChange(roleId, 'offshore', parseInt(e.target.value, 10))}
          />
        </div>
      </div>

      <Tooltip.Simple label="Delete role">
        <button
          className="da-option-row__delete"
          onClick={() => handleDeleteRole(roleId)}
          aria-label={`Delete role ${roleId}`}
        >
          ×
        </button>
      </Tooltip.Simple>
    </div>
  );
}

function AddRoleRow({
  onAdd,
  autoOpen,
  onCancel,
}: {
  onAdd: (roleId: string, roleName: string, level: string, category: string) => void;
  autoOpen: boolean;
  onCancel: () => void;
}) {
  const [open, setOpen] = React.useState(autoOpen);
  const [value, setValue] = React.useState('');
  const [level, setLevel] = React.useState('mid');
  const [category, setCategory] = React.useState('functional');

  React.useEffect(() => {
    setOpen(autoOpen);
  }, [autoOpen]);

  function handleAdd() {
    const trimmedName = value.trim();
    if (trimmedName) {
      const generatedId = toSnakeCase(trimmedName);
      onAdd(generatedId, trimmedName, level, category);
    }
    setValue('');
    setOpen(false);
    onCancel();
  }

  function handleCancel() {
    setValue('');
    setOpen(false);
    onCancel();
  }

  if (!open) {
    return (
      <button
        className="da-add-option-row"
        onClick={() => setOpen(true)}
        aria-label="Add a new role"
      >
        <span className="da-add-option-row__plus">+</span>
        <span className="da-add-option-row__label">Add a role</span>
      </button>
    );
  }

  return (
    <div className="da-add-option-row da-add-option-row--open da-add-option-row--wrap">
      <input
        className="da-option-row__edit-input da-option-row__role-input"
        value={value}
        autoFocus
        placeholder="Role Name..."
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleAdd();
          if (e.key === 'Escape') handleCancel();
        }}
        aria-label="New role name"
      />
      <div className="da-option-row__role-meta-select">
        <Select value={level} onValueChange={setLevel} size="sm">
          {LEVEL_OPTIONS.map((opt) => (
            <SelectItem key={opt} value={opt}>
              {opt.charAt(0).toUpperCase() + opt.slice(1)}
            </SelectItem>
          ))}
        </Select>
      </div>
      <div className="da-option-row__role-meta-select da-option-row__role-meta-select--wide">
        <Select value={category} onValueChange={setCategory} size="sm">
          {CATEGORY_OPTIONS.map((opt) => (
            <SelectItem key={opt} value={opt}>
              {opt.charAt(0).toUpperCase() + opt.slice(1)}
            </SelectItem>
          ))}
        </Select>
      </div>

      <div className="da-add-option-row__actions ml-auto">
        <button
          type="button"
          className="da-add-option-row__btn da-add-option-row__btn--add"
          onClick={handleAdd}
          disabled={!value.trim()}
        >
          Add
        </button>
        <button
          type="button"
          className="da-add-option-row__btn da-add-option-row__btn--cancel"
          onClick={handleCancel}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export function RateCardPane({
  draftConfig,
  handleRateChange,
  handleUpdateDescription,
  handleAddRole,
  handleUpdateRoleMeta,
  handleDeleteRole,
  handleReorderRoles,
  isEditing,
  isCreatingRole,
  onCancelCreateRole,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  draftConfig: any;
  handleRateChange: (roleId: string, type: 'onshore' | 'offshore', value: number) => void;
  handleUpdateDescription?: (description: string) => void;
  handleAddRole: (roleId: string, roleName: string, level: string, category: string) => void;
  handleUpdateRoleMeta: (
    roleId: string,
    newRoleName: string,
    level: string,
    category: string
  ) => void;
  handleDeleteRole: (roleId: string) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleReorderRoles: (newRates: any[]) => void;
  isEditing: boolean;
  isCreatingRole: boolean;
  onCancelCreateRole: () => void;
}) {
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const dragIndexRef = React.useRef<number | null>(null);

  if (!draftConfig) return null;

  const rates = Array.isArray(draftConfig.rates) ? draftConfig.rates : [];

  function handleDragStart(idx: number) {
    dragIndexRef.current = idx;
  }

  function handleDragOver(idx: number) {
    if (dragIndexRef.current !== null && dragIndexRef.current !== idx) {
      setDragOverIdx(idx);
    }
  }

  function handleDragLeave() {
    setDragOverIdx(null);
  }

  function handleDrop(targetIdx: number) {
    const sourceIdx = dragIndexRef.current;
    dragIndexRef.current = null;
    setDragOverIdx(null);

    if (sourceIdx === null || sourceIdx === targetIdx) return;

    const reordered = [...rates];
    const [moved] = reordered.splice(sourceIdx, 1);
    if (moved) {
      reordered.splice(targetIdx, 0, moved);
      handleReorderRoles(reordered);
    }
  }

  return (
    <div className="ptp-est-config-pane">
      <div className="da-detail">
        {isEditing ? (
          <div className="da-detail__description-edit">
            <TextArea
              value={draftConfig.description || ''}
              onChange={(e) => handleUpdateDescription?.(e.target.value)}
              placeholder="Enter rate card description"
              rows={1}
            />
          </div>
        ) : (
          draftConfig.description && (
            <div className="da-detail__description">{draftConfig.description}</div>
          )
        )}
        <div className="da-option-list mx-6 mb-6 mt-6">
          {rates.length === 0 && !isEditing && (
            <p className="da-option-list__empty">No roles defined.</p>
          )}
          {isEditing
            ? rates.map((r: any, idx: number) => (
                <RoleRateEditRow
                  key={r.role_id}
                  roleId={r.role_id}
                  rates={r}
                  index={idx}
                  isDragOver={dragOverIdx === idx}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  handleRateChange={handleRateChange}
                  handleUpdateRoleMeta={handleUpdateRoleMeta}
                  handleDeleteRole={handleDeleteRole}
                />
              ))
            : rates.map((r: any) => (
                <RoleRateReadRow key={r.role_id} roleId={r.role_id} rates={r} />
              ))}
          {isEditing && (
            <AddRoleRow
              autoOpen={isCreatingRole}
              onCancel={onCancelCreateRole}
              onAdd={(roleId, roleName, level, category) => {
                // `lead_consultant` and `lead-consultant` are the same role.
                const existing = matchRoleKey(
                  roleId,
                  rates.map((r: any) => r.role_id)
                );
                if (existing.match || existing.duplicates.length > 0) {
                  alert('A role with this ID already exists. Try a different name.');
                  return;
                }
                handleAddRole(roleId, roleName, level, category);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
